import React, { useEffect, useState } from 'react';
import { PageContentWrapper, LeftTopWrapper, LeftContentWrapper, RightContentWrapper, LeftBottomWrapper, RightWrapper } from './TankCleaning.styles';
import { useRotationStore } from '../../context/RotationContext';
import { useEventStore } from '../../context/EventContext';
import ReportingContextInfo from '../../components/tank-cleaning/common/ReportingContextInfo';
import { RotationTreeEnum } from '../../enums/rotationTreeEnum';
import { useVesselStore } from '../../context/VesselContext';
import useRotation from '../../app/useRotation';
import { IRotationNode } from '../../types/ViewModel/IRotationNode';
import useEvent from '../../app/useEvent';
import queryParamsUtils from '../../utils/queryParamsUtils';
import { PortCall } from '../../types/ViewModel/PortCall';
import useGetRotation from '../../api/useGetRotation';
import { Rotation } from '../../types/ViewModel/Rotation';
import RotationFactory from '../../factory/ReportEvents/Rotation/RotationFactory';
import TankCleaningRotationTreeComponent from '../../components/tank-cleaning/TankCleaningRotationTree';
import ReportRob from '../../components/tank-cleaning/report-rob/ReportRob';
import { useTankCleaningStore } from '../../context/TankCleaningContext';
import { ReportLocationEnum } from '../../enums/reportLocationEnum';
import useGetChemicalData from '../../api/useGetChemicalData';
import ChemicalOrder from '../../components/tank-cleaning/chemical-order/ChemicalOrder';
import mapTankCleaningUtils from '../../utils/mapTankCleaningUtils';
import { ChemicalOrderReport } from '../../types/ViewModel/TankCleaning/Order/ChemicalOrderReport';
import { ChemicalRobReport } from '../../types/ViewModel/TankCleaning/ROB/ChemicalRobReport';
import { ChemicalOrderPortCall } from '../../types/ViewModel/TankCleaning/Order/ChemicalOrderPortCall';
import SelectedRotationInfo from '../../components/tank-cleaning/common/SelectedRotationInfo';
import useGetLatestVoyage from '../../api/useGetLatestVoyage';
import useGetTankersEventForVoyage from '../../api/useGetTankersEventForVoyage';
import TankersEventFactory from '../../factory/TankersEventFactory';
import { useQueryParamsHandler } from 'odfjell-query-manager';
import { useStowageStore } from 'src/context/StowageContext';
import VoyageSelectionPanel from 'src/components/stowage/common/voyageSelectionPanel/VoyageSelectionPanel';
import { VoyageOption } from 'src/types/ViewModel/VoyageOption';
import { generateUnifiedVoyageOptionsList } from 'src/utils/voyageSelectionUtils';
import useCheckStowageIntegration from 'src/app/useCheckStowageIntegration';
import useAppFeature from 'src/app/useAppFeature';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import ConsumablesHeader from '../../components/tank-cleaning/ConsumablesHeader';

const TankCleaningComponent: React.FC = () => {
    const [rotationTree, setRotationTree] = useState<Rotation | null>(null);
    const [voyageOptionsList, setVoyageOptionsList] = useState<VoyageOption[]>([]);
    const { newRotationFromCode, newRotationFromCodeExcludeMovedAndDeleted, updateRotationWithConsumables } = RotationFactory();
    const { getExtendedRotation } = useGetRotation();
    const { selectedVessel } = useVesselStore();
    const { fullEventArray, eventDataForVoyageList, setFullEventArray, setDeltaEventArray, deltaEventArray, latestVoyageData } = useEventStore();
    const {
        rotation,
        setUpdatedRotation,
        setCurrentPortCall,
        setNextPortCall,
        setPreviousPortCall,
        updatedRotation,
        selectedNode,
        setSelectedNode,
        currentPortCall,
        selectedStowageVoyages,
        setSelectedStowageVoyages,
    } = useRotationStore();
    const { getPortCall, getNextPortCall, getPreviousPortCall } = useRotation();
    const { getDefaultRotationTree, getRotationTreeByVoyages, getCurrentPortCall } = useEvent();
    const { setQueryParamPortCall, setQueryFromSelectedNode } = queryParamsUtils();
    const { getQueryParamValueFromUrl } = useQueryParamsHandler();

    const {
        chemicalList,
        chemicalCategoryList,
        mappedChemicalList,
        selectedTankCleaningPortCall,
        setSelectedTankCleaningPortCall,
        setPreviousTankCleaningPortCall,
        isOrderPanelVisible,
        selectedChemicalRobForPortCall,
        selectedChemicalOrderForPortCall,
        setSelectedChemicalRobForPortCall,
        setSelectedChemicalOrderForPortCall,
        selectedChemicalOrderForPortCallList,
        setSelectedChemicalOrderForPortCallList,
        setChemicalOrderUpdateReportData,
        setChemicalRobUpdateReportData,
        setIsOrderPanelVisible,
        selectedChemicalOrderId,
        setSelectedChemicalOrderId,
        chemicalReportType,
        setChemicalReportType,
        isNewOrder,
        shouldCopyLastRob,
        setShouldCopyLastRob,
        chemicalRobForPortCallList,
        chemicalOrderForPortCallList,
    } = useTankCleaningStore();

    const {
        generateChemicalOrderReport,
        generateChemicalOrderUpdateReportData,
        generateChemicalRobReport,
        generateChemicalRobUpdateReportData,
        getLatestUpdatedChemicalOrder,
    } = mapTankCleaningUtils();
    const [chemicalOrderFormData, setChemicalOrderFormData] = useState<ChemicalOrderReport | null>(null);
    const [chemicalRobFormData, setChemicalRobFormData] = useState<ChemicalRobReport | null>(null);
    const [previousPortCallWithRob, setPreviousPortCallWithRob] = useState<PortCall>();
    const { setQueryParamChemicalOrder, resetQueryParamPortCall } = queryParamsUtils();
    const { getLatestVoyage } = useGetLatestVoyage();
    const { getChemicalConfigData } = useGetChemicalData();
    const { getChemicalData } = useGetChemicalData();
    const { getEventsForVoyageList } = useGetTankersEventForVoyage();
    const { createNewEventList } = TankersEventFactory();
    const { queryParamUpdate } = useQueryParamsHandler();
    const { loadedMasterStowagePlan, activeStowagePlan } = useStowageStore();
    const { checkLoadedStowageIntegration } = useCheckStowageIntegration();
    const { isFeatureEnabled } = useAppFeature();

    // =============  Use Effects ================

    useEffect(() => {
        // Wait for stowage plan to load if stowage integration is enabled
        if (!selectedVessel || checkLoadedStowageIntegration(selectedVessel.imoNumber, loadedMasterStowagePlan)) return;

        if (
            !rotation ||
            (checkLoadedStowageIntegration(selectedVessel.imoNumber, loadedMasterStowagePlan) &&
                (loadedMasterStowagePlan.lastUpdatedUtc !== rotation.lastUpdatedUtc || loadedMasterStowagePlan.title !== rotation.stowagePlanName)) ||
            // CRITICAL: If stowage integration is enabled but rotation doesn't have merged stowage data, refresh
            (isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber: selectedVessel.imoNumber, date: new Date() }) &&
                loadedMasterStowagePlan &&
                rotation &&
                rotation.stowagePlanName !== loadedMasterStowagePlan.title)
        ) {
            getLatestVoyage(selectedVessel.imoNumber);
            getExtendedRotation(selectedVessel.imoNumber);
            getChemicalData(selectedVessel.imoNumber);
            getChemicalConfigData();
        } else if (!mappedChemicalList || !chemicalRobForPortCallList || !chemicalOrderForPortCallList) {
            getChemicalConfigData();
            getChemicalData(selectedVessel.imoNumber);
            getLatestVoyage(selectedVessel.imoNumber);
        }
    }, [selectedVessel, loadedMasterStowagePlan]);

    useEffect(() => {
        if (selectedTankCleaningPortCall === null) {
            setSelectedChemicalRobForPortCall(null);
            setSelectedChemicalOrderForPortCallList([]);
        }
        if (
            selectedTankCleaningPortCall &&
            selectedTankCleaningPortCall.portCallId &&
            selectedVessel &&
            mappedChemicalList &&
            mappedChemicalList.length > 0
        ) {
            // ROB
            setSelectedChemicalRobForPortCall(selectedTankCleaningPortCall.chemicalRobForPortCall);
            if (!selectedTankCleaningPortCall.chemicalRobForPortCall) {
                generateChemicalRobFormDataAndUpdateModel();
            }

            // ORDER
            setSelectedChemicalOrderForPortCallList([...selectedTankCleaningPortCall.chemicalOrderForPortCallList]);

            // PREV ROB
            const _previousPortCall = getPreviousPortCallWithRob(selectedTankCleaningPortCall);
            setPreviousPortCallWithRob(_previousPortCall ? { ..._previousPortCall } : undefined);

            updateChemicalReportType(chemicalReportType);
        }
    }, [selectedTankCleaningPortCall, mappedChemicalList, chemicalReportType]);

    useEffect(() => {
        if (
            selectedChemicalOrderId &&
            chemicalReportType === 'order' &&
            selectedChemicalOrderForPortCallList &&
            selectedChemicalOrderForPortCallList.length > 0
        ) {
            updateSelectedChemicalOrderForPortCall();
        }
    }, [selectedChemicalOrderId]);

    useEffect(() => {
        if (selectedVessel && selectedTankCleaningPortCall) {
            updateRotationTreeStatus('ORDER');
            updateSelectedChemicalOrderForPortCall();
        }
    }, [selectedChemicalOrderForPortCallList]);

    useEffect(() => {
        if (isNewOrder) {
            updateSelectedChemicalOrderForPortCall();
        }
    }, [isNewOrder]);

    useEffect(() => {
        if (selectedVessel && selectedTankCleaningPortCall && mappedChemicalList && mappedChemicalList.length > 0) {
            updateRotationTreeStatus('ROB');
            generateChemicalRobFormDataAndUpdateModel();
        }
    }, [selectedChemicalRobForPortCall, mappedChemicalList]);

    useEffect(() => {
        if (!currentPortCall || !selectedVessel) return;

        // FIX: Use isFeatureEnabled directly instead of checkLoadedStowageIntegration
        // checkLoadedStowageIntegration returns true when stowage is enabled AND plan is NOT loaded yet
        // We need hasStowageIntegration to be true when stowage is enabled AND plan IS loaded
        const hasStowageIntegration = isFeatureEnabled({
            featureEnum: AppFeatureEnum.StowageIntegration,
            imoNumber: selectedVessel.imoNumber,
            date: new Date(),
        });

        // Use activeStowagePlan when available (has fresher rotation data from stowage module),
        // fallback to loadedMasterStowagePlan for non-stowage page scenarios
        const stowagePlanForVoyages = activeStowagePlan ?? loadedMasterStowagePlan;
        const voyageOptions = generateUnifiedVoyageOptionsList(
            hasStowageIntegration,
            updatedRotation,
            currentPortCall.voyageNumber1,
            stowagePlanForVoyages,
            selectedVessel
        );

        setVoyageOptionsList(voyageOptions);
    }, [updatedRotation, currentPortCall, loadedMasterStowagePlan, activeStowagePlan, selectedVessel]);

    useEffect(() => {
        if (rotation && (!fullEventArray || fullEventArray.length === 0)) {
            // initially load event data for latest voyage (most recent voyage where there exist any events).
            // also include the voyage before the latest.
            // if no latest voyage can be found (should only happen with vessels without any events, likely new vessel),
            // try to retrieve the 4 latest voyages
            //resetContext();
            const voyageList = new Set<string>();
            if (latestVoyageData?.voyageNumber1) {
                const latestVoyage = latestVoyageData.voyageNumber1.toString();
                if (latestVoyage && latestVoyage.length > 0) voyageList.add(latestVoyageData.voyageNumber1.toString());
            }
            const allUniqueVoyageNumbers = getAllUniqueVoyageNumbers(rotation, true);
            const latestUniqueVoyageNumbers = allUniqueVoyageNumbers.slice(0, Math.min(4, allUniqueVoyageNumbers.length - 1));
            const latestVoyageIndex = allUniqueVoyageNumbers.findIndex((x) => x === latestVoyageData?.voyageNumber1?.toString());
            const previousVoyage = latestVoyageIndex > -1 && allUniqueVoyageNumbers?.[latestVoyageIndex + 1];
            if (previousVoyage) {
                voyageList.add(previousVoyage);
            } else {
                latestUniqueVoyageNumbers.forEach(voyageList.add, voyageList);
            }
            getEventsForVoyageList(Array.from(voyageList.values()));
        }
    }, [rotation, latestVoyageData]);

    useEffect(() => {
        if (deltaEventArray && deltaEventArray.length > 0) {
            // Filter delta with respect to events previously retrieved for selected voyages
            const deltaEventsForPreviouslyRetrievedVoyages = deltaEventArray.filter(
                (o) =>
                    eventDataForVoyageList?.imoNumber &&
                    eventDataForVoyageList.imoNumber === selectedVessel.imoNumber &&
                    eventDataForVoyageList.voyageNumbers.some((e) => +e === o.voyageNumber1)
            );
            if (deltaEventsForPreviouslyRetrievedVoyages && deltaEventsForPreviouslyRetrievedVoyages.length > 0) {
                const oldEventsMinusDelta = fullEventArray
                    ? fullEventArray.filter((oldEv) => !deltaEventsForPreviouslyRetrievedVoyages.some((e) => e.eventId === oldEv.eventId))
                    : [];
                const tempEvents = [...oldEventsMinusDelta, ...deltaEventsForPreviouslyRetrievedVoyages.filter((e) => !e.deleted)];
                const updatedEventList = createNewEventList(tempEvents);

                setFullEventArray(updatedEventList);
                setDeltaEventArray([]);
            }
            // if deltaEventArray is successfully loaded with no events, set fullEventArray to empty array
        } else if (deltaEventArray && deltaEventArray.length == 0 && !fullEventArray) setFullEventArray([]);
    }, [deltaEventArray]);

    useEffect(() => {
        if (rotation && fullEventArray && chemicalRobForPortCallList && chemicalOrderForPortCallList) {
            updateRotation();
        }
    }, [rotation, fullEventArray, chemicalRobForPortCallList, chemicalOrderForPortCallList]);

    useEffect(() => {
        if (queryParamUpdate && selectedVessel) {
            if (!rotation) {
                // Refresh (F5) gets the stowage plan
                getExtendedRotation(selectedVessel.imoNumber);
            } else {
                handleQueryPortLog();
            }
        }
    }, [queryParamUpdate]);

    useEffect(() => {
        if (updatedRotation && selectedVessel) {
            handleQueryPortLog();
            updateToFutureSelectedRotations(updatedRotation);
        }
    }, [updatedRotation]);

    useEffect(() => {
        updateRotationTree(updatedRotation);
    }, [selectedStowageVoyages]);

    useEffect(() => {
        const _previousPortCallWithRob = getPreviousPortCallWithRob(selectedTankCleaningPortCall);
        if (shouldCopyLastRob && _previousPortCallWithRob && _previousPortCallWithRob.chemicalRobForPortCall) {
            const _chemicalRobFormData = generateChemicalRobReport(
                selectedTankCleaningPortCall.portCallId,
                previousPortCallWithRob.chemicalRobForPortCall,
                null,
                mappedChemicalList,
                chemicalCategoryList
            );

            _chemicalRobFormData.portCallId = selectedTankCleaningPortCall.portCallId;
            setChemicalRobFormData({ ..._chemicalRobFormData });

            const _chemicalRobUpdateReportData = generateChemicalRobUpdateReportData(
                previousPortCallWithRob.chemicalRobForPortCall,
                selectedTankCleaningPortCall.portCallId,
                selectedVessel.imoNumber,
                selectedChemicalRobForPortCall?.chemicalRobId ? selectedChemicalRobForPortCall?.chemicalRobId : 0,
                mappedChemicalList
            );

            if (_chemicalRobUpdateReportData) {
                setChemicalRobUpdateReportData({ ..._chemicalRobUpdateReportData });
            }
        }
        setShouldCopyLastRob(null);
    }, [shouldCopyLastRob]);

    const getAllUniqueVoyageNumbers = (rotation: Rotation, includeLatest = false): string[] => {
        const voyList: string[] = [];
        rotation.portCallArray.forEach((pc) => {
            if (pc.voyageNumber1 && !voyList.find((o) => o === pc.voyageNumber1)) voyList.push(pc.voyageNumber1);
            if (pc.voyageNumber2 && !voyList.find((o) => o === pc.voyageNumber2)) voyList.push(pc.voyageNumber2);
        });
        if (includeLatest) {
            return voyList.sort().reverse();
        }
        return voyList
            .filter((o) => !latestVoyageData || +o !== latestVoyageData.voyageNumber1)
            .sort()
            .reverse();
    };

    const updateChemicalReportType = (reportType: string | null) => {
        if (reportType === 'rob' || reportType === null || reportType === undefined) {
            setIsOrderPanelVisible(false);
        } else if (reportType === 'order') {
            setIsOrderPanelVisible(true);
        }
    };

    const updateToFutureSelectedRotations = (rotation: Rotation) => {
        const currentAndFutureVoyages: string[] = [];
        const currentVoyageNumber = currentPortCall?.voyageNumber1;
        rotation.portCallArray?.forEach((p) => {
            if (
                Number(p.voyageNumber1) >= Number(currentVoyageNumber) &&
                currentAndFutureVoyages.findIndex((s) => s.toLowerCase() === p.voyageNumber1.toLowerCase()) === -1
            ) {
                currentAndFutureVoyages.push(p.voyageNumber1);
            } else if (
                p.voyageNumber2 &&
                Number(p.voyageNumber2) >= Number(currentVoyageNumber) &&
                currentAndFutureVoyages.findIndex((s) => s.toLowerCase() === p.voyageNumber2?.toLowerCase()) === -1
            ) {
                currentAndFutureVoyages.push(p.voyageNumber2);
            }
        });

        setSelectedStowageVoyages([...currentAndFutureVoyages].sort());
    };

    const getPreviousPortCallWithRob = (selectedTankCleaningPortCall: PortCall) => {
        if (!updatedRotation?.portCallArray || !selectedTankCleaningPortCall) return;
        let indexOfPortCall = updatedRotation.portCallArray.findIndex((pc) => pc.portCallId === selectedTankCleaningPortCall?.portCallId);
        if (indexOfPortCall < 1) return;

        indexOfPortCall--;
        let _previousPortCall: PortCall | null = null;
        while (!_previousPortCall && indexOfPortCall > 0) {
            const pc = updatedRotation.portCallArray[indexOfPortCall];

            if (pc.chemicalRobForPortCall) {
                _previousPortCall = pc;
            }
            indexOfPortCall--;
        }
        return _previousPortCall;
    };

    const updateSelectedChemicalOrderForPortCall = () => {
        if (
            selectedTankCleaningPortCall &&
            selectedTankCleaningPortCall.portCallId &&
            selectedVessel &&
            chemicalReportType &&
            chemicalReportType === 'order'
        ) {
            let _selectedChemicalOrderForPortCall: ChemicalOrderPortCall | undefined = undefined;
            const query = new URLSearchParams(window.location.search);
            const _chemicalOrderId = query.get('chemicalOrderId');

            if (
                selectedChemicalOrderId &&
                Number(_chemicalOrderId) === Number(selectedChemicalOrderId) &&
                !isNewOrder &&
                selectedChemicalOrderForPortCallList &&
                selectedChemicalOrderForPortCallList.length > 0
            ) {
                _selectedChemicalOrderForPortCall = selectedChemicalOrderForPortCallList.find((c) => c.chemicalOrderId === selectedChemicalOrderId);
                if (_selectedChemicalOrderForPortCall) {
                    setSelectedChemicalOrderForPortCall({ ..._selectedChemicalOrderForPortCall });
                    generateChemicalOrderFormDataAndUpdateModel(_selectedChemicalOrderForPortCall);
                } else {
                    setSelectedChemicalOrderForPortCall(null);
                    console.error('updateSelectedChemicalOrderForPortCall error - could not find chemical order');
                }
            } else if (!isNewOrder && selectedChemicalOrderForPortCallList && selectedChemicalOrderForPortCallList.length > 0) {
                if (selectedChemicalOrderId === null) {
                    _selectedChemicalOrderForPortCall = getLatestUpdatedChemicalOrder(selectedChemicalOrderForPortCallList);
                    if (_selectedChemicalOrderForPortCall) {
                        setQueryParamChemicalOrder(_selectedChemicalOrderForPortCall.chemicalOrderId.toString());
                    }
                } else if (selectedChemicalOrderId && Number(_chemicalOrderId) !== Number(selectedChemicalOrderId)) {
                    setQueryParamChemicalOrder(selectedChemicalOrderId.toString());
                }
            } else if (
                selectedChemicalOrderId === null &&
                !isNewOrder &&
                (!selectedChemicalOrderForPortCallList || selectedChemicalOrderForPortCallList.length === 0)
            ) {
                setSelectedChemicalOrderForPortCall(null);
                generateChemicalOrderFormDataAndUpdateModel(null);
            } else if (isNewOrder) {
                setSelectedChemicalOrderForPortCall(null);
                generateChemicalOrderFormDataAndUpdateModel(null);
            } else {
                setSelectedChemicalOrderForPortCall(null);
                console.error('updateSelectedChemicalOrderForPortCall error - selectedChemicalOrderForPortCall is set to null');
            }
        }
    };

    const generateChemicalOrderFormDataAndUpdateModel = (chemicalOrderForPortCall: ChemicalOrderPortCall | null) => {
        if (
            selectedVessel &&
            selectedTankCleaningPortCall &&
            selectedTankCleaningPortCall.portCallId &&
            chemicalCategoryList &&
            chemicalCategoryList.length > 0
        ) {
            const _chemicalOrderFormData = generateChemicalOrderReport(
                chemicalOrderForPortCall,
                selectedChemicalOrderForPortCallList,
                chemicalList,
                chemicalCategoryList
            );
            setChemicalOrderFormData({ ..._chemicalOrderFormData });

            const _chemicalOrderUpdateReportData = generateChemicalOrderUpdateReportData(
                _chemicalOrderFormData,
                chemicalOrderForPortCall,
                selectedTankCleaningPortCall.portCallId,
                selectedVessel.imoNumber,
                chemicalList
            );

            if (_chemicalOrderUpdateReportData) {
                setChemicalOrderUpdateReportData({ ..._chemicalOrderUpdateReportData });
            }
        }
    };

    const generateChemicalRobFormDataAndUpdateModel = () => {
        if (
            selectedTankCleaningPortCall &&
            selectedTankCleaningPortCall.portCallId &&
            selectedVessel &&
            chemicalCategoryList &&
            chemicalCategoryList.length > 0 &&
            mappedChemicalList &&
            mappedChemicalList.length > 0
        ) {
            const _chemicalRobFormData = generateChemicalRobReport(
                selectedTankCleaningPortCall.portCallId,
                selectedChemicalRobForPortCall,
                null,
                mappedChemicalList,
                chemicalCategoryList
            );
            setChemicalRobFormData({ ..._chemicalRobFormData });

            const _chemicalRobUpdateReportData = generateChemicalRobUpdateReportData(
                selectedChemicalRobForPortCall,
                selectedTankCleaningPortCall.portCallId,
                selectedVessel.imoNumber,
                selectedChemicalRobForPortCall?.chemicalRobId ? selectedChemicalRobForPortCall?.chemicalRobId : 0,
                mappedChemicalList
            );

            if (_chemicalRobUpdateReportData) {
                setChemicalRobUpdateReportData({ ..._chemicalRobUpdateReportData });
            }
        }
    };

    const updateRotationTreeStatus = (type: string) => {
        if (selectedVessel && selectedTankCleaningPortCall && updatedRotation) {
            const _updatedRotation = updatedRotation;
            if (_updatedRotation?.portCallArray && _updatedRotation?.portCallArray.length > 0) {
                const updatedPortCall = _updatedRotation?.portCallArray.find(
                    (pc) => pc.portCallId === selectedTankCleaningPortCall.portCallId && pc.reportLocation === ReportLocationEnum.Port
                );
                if (updatedPortCall) {
                    if (type === 'ROB') {
                        updatedPortCall.chemicalRobForPortCall = selectedChemicalRobForPortCall;
                    } else if (type === 'ORDER') {
                        updatedPortCall.chemicalOrderForPortCallList = selectedChemicalOrderForPortCallList;
                    }

                    updateRotationTree(_updatedRotation);
                    setUpdatedRotation(_updatedRotation);
                }
            }
        }
    };

    const updateCurrentPortCall = (rot: Rotation) => {
        const currentPortCall = getCurrentPortCall(rot) ?? null;
        setCurrentPortCall(currentPortCall);

        if (currentPortCall) {
            const nextPortCall = getNextPortCall(currentPortCall?.portCallId, rot);
            setNextPortCall(nextPortCall);
            const previousPortCall = getPreviousPortCall(currentPortCall?.portCallId);
            setPreviousPortCall(previousPortCall);
            setPreviousTankCleaningPortCall(previousPortCall);
        } else {
            setNextPortCall(null);
        }
    };

    const updateRotation = () => {
        if (rotation) {
            // Add rotation nodes for moved/deleted port calls/berth calls/cargo operations
            let newRot = newRotationFromCodeExcludeMovedAndDeleted(rotation);
            newRot = updateRotationWithConsumables(newRot, chemicalRobForPortCallList, chemicalOrderForPortCallList);

            setUpdatedRotation({ ...newRot });
            updateCurrentPortCall({ ...newRot });
        }
    };

    const updateAndModifyRotationTree = (rot: Rotation) => {
        const newRot = newRotationFromCode(rot);
        setRotationTree({ ...newRot });
    };

    const updateRotationTree = (rot: Rotation | null) => {
        if (selectedStowageVoyages?.length > 0) {
            const newRot = getRotationTreeByVoyages(selectedStowageVoyages, rot); // Uses UpdatedRotation
            if (newRot) {
                updateAndModifyRotationTree(newRot);
            }
        } else {
            const newRot = getDefaultRotationTree(rot); // Uses UpdatedRotation
            if (newRot) {
                updateAndModifyRotationTree(newRot);
            }
        }
    };

    const isSamePortCall = (node: IRotationNode, selNode: IRotationNode) => {
        return node.id === selNode.id && node.reportLocation === selNode.reportLocation;
    };

    const updateSelectedNode = (node: IRotationNode): boolean => {
        if (!selectedNode || selectedNode.type != node.type) {
            setSelectedNode(node);
            setSelectedTankCleaningPortCall(node as PortCall);
            return true;
        } else if (selectedNode && !selectedTankCleaningPortCall) {
            setSelectedTankCleaningPortCall(node as PortCall);
        } else {
            switch (node.type) {
                case RotationTreeEnum.PortCall:
                case RotationTreeEnum.Sea:
                    if (!isSamePortCall(node, selectedNode)) {
                        setSelectedNode(node);
                        const _portCall = updatedRotation?.portCallArray.find((pc) => pc.portCallId === node.id);
                        if (_portCall) {
                            setSelectedTankCleaningPortCall(_portCall);
                        }

                        return true;
                    }
                    break;
            }
        }
        return false;
    };

    const handleQueryPortLog = () => {
        const portCallId = getQueryParamValueFromUrl('portCallId');
        const reportType = getQueryParamValueFromUrl('reportType');
        const chemicalOrderId = getQueryParamValueFromUrl('chemicalOrderId');

        const isQuerySet = false;
        if (portCallId) {
            const pc = getPortCall(updatedRotation, +portCallId, ReportLocationEnum.Port);
            if (pc) {
                updateSelectedNode(pc);
                updateSelectedRotations(pc);
                if (!isQuerySet) {
                    updateRotationTree(updatedRotation);
                }

                if (reportType) {
                    setChemicalReportType(reportType);
                } else {
                    setChemicalReportType('rob');
                }

                if (chemicalOrderId) {
                    setSelectedChemicalOrderId(+chemicalOrderId);
                } else {
                    setSelectedChemicalOrderId(null);
                }
            } else {
                setSelectedNode(null);
                setQueryFromSelectedNode(null);
                updateRotationTree(updatedRotation);
            }
        } else if (selectedNode) {
            // Click 'Report events' in left side panel
            setQueryFromSelectedNode(selectedNode);
        } else if (currentPortCall && (!selectedStowageVoyages || selectedStowageVoyages.length === 0)) {
            setQueryParamPortCall(currentPortCall.portCallId?.toString(), currentPortCall?.reportLocation);
        }
    };

    const updateSelectedRotations = (portCall: PortCall) => {
        let _selectedVoyages: string[] = [];
        const _defRot = getDefaultRotationTree(updatedRotation);

        if (selectedStowageVoyages) {
            _selectedVoyages = [...selectedStowageVoyages];
        }

        if (selectedStowageVoyages?.length > 0) {
            let isVoyageInSelectedRotations;

            if (
                portCall.voyageNumber2 &&
                (selectedStowageVoyages.includes(portCall.voyageNumber1) || selectedStowageVoyages.includes(portCall.voyageNumber2))
            ) {
                isVoyageInSelectedRotations = true;
            } else {
                isVoyageInSelectedRotations = selectedStowageVoyages.includes(portCall.voyageNumber1);
            }

            if (!isVoyageInSelectedRotations) {
                _selectedVoyages.push(portCall.voyageNumber1);
            } else {
                _selectedVoyages = [...selectedStowageVoyages];
            }
        } else if (_defRot && _defRot.portCallArray.length > 0 && _defRot.portCallArray.find((o) => o.portCallId === +portCall.portCallId)) {
            // When the default rotation tree has portcalls, gather their voyage numbers
            if (_defRot) {
                const uniqueVoyages = new Set<string>();

                _defRot.portCallArray.forEach((portCall) => {
                    if (portCall.voyageNumber1) {
                        uniqueVoyages.add(portCall.voyageNumber1);
                    }
                });

                // Add all unique voyages to the selected voyages
                _selectedVoyages = Array.from(uniqueVoyages);
            }
        } else {
            _selectedVoyages.push(portCall.voyageNumber1);
        }

        if (_selectedVoyages && _selectedVoyages.length > 0) {
            setSelectedStowageVoyages([..._selectedVoyages].sort());
        } else {
            setSelectedStowageVoyages([]);
        }
    };

    const getOrderIndex = () => {
        return selectedChemicalOrderForPortCallList.findIndex((pc) => pc.chemicalOrderId === selectedChemicalOrderForPortCall?.chemicalOrderId);
    };

    const clearFiltersCallback = () => {
        resetQueryParamPortCall();
        setSelectedNode(null);
    };

    const createQueryParamForVoyage = (selectedVoyages: string[], selectedVoyageNumber: string, voyageNrOn: boolean) => {
        if (!selectedVoyages || selectedVoyages.length === 0) {
            resetQueryParamPortCall();
            setSelectedNode(null);
        } else {
            if (voyageNrOn) {
                const pc = updatedRotation?.portCallArray.find((o) => o.voyageNumber1 === selectedVoyageNumber);
                if (pc) {
                    setQueryFromSelectedNode(pc);
                }
            } else {
                const pc = updatedRotation?.portCallArray.find((o) => o.voyageNumber1 === selectedVoyages[0]);
                if (pc) {
                    setQueryFromSelectedNode(pc);
                }
            }
        }
    };

    const onSelectVoyageCallback = (selectedVoyageNumbers: string[], selectedVoyageNumber: string, isVoyageNumberOn: boolean) => {
        createQueryParamForVoyage(selectedVoyageNumbers, selectedVoyageNumber, isVoyageNumberOn);
    };

    return (
        <PageContentWrapper>
            <LeftContentWrapper>
                <LeftTopWrapper>
                    <VoyageSelectionPanel
                        voyageOptionsList={voyageOptionsList}
                        onClearFiltersCallback={clearFiltersCallback}
                        onSelectVoyageCallback={onSelectVoyageCallback}
                    />
                    <RightWrapper>
                        <ReportingContextInfo />
                        <SelectedRotationInfo />
                    </RightWrapper>
                </LeftTopWrapper>
                <LeftBottomWrapper>
                    {rotationTree && selectedTankCleaningPortCall && selectedTankCleaningPortCall.portCallId && (
                        <TankCleaningRotationTreeComponent rotationTree={rotationTree} selectedPortCall={selectedTankCleaningPortCall} />
                    )}
                </LeftBottomWrapper>
            </LeftContentWrapper>
            {selectedVessel && selectedTankCleaningPortCall && selectedTankCleaningPortCall.portCallId && (
                <RightContentWrapper>
                    <ConsumablesHeader selectedTankCleaningPortCall={selectedTankCleaningPortCall}></ConsumablesHeader>
                    {isOrderPanelVisible && selectedTankCleaningPortCall && chemicalOrderFormData && (
                        <ChemicalOrder
                            chemicalOrderFormData={chemicalOrderFormData}
                            selectedPortCallId={selectedTankCleaningPortCall?.portCallId}
                            previousPortCallWithRob={previousPortCallWithRob}
                            imoNumber={selectedVessel.imoNumber}
                            orderIndex={getOrderIndex()}
                        ></ChemicalOrder>
                    )}
                    {!isOrderPanelVisible && selectedTankCleaningPortCall && chemicalRobFormData && (
                        <ReportRob
                            chemicalRobFormData={chemicalRobFormData}
                            selectedPortCallId={selectedTankCleaningPortCall?.portCallId}
                            previousPortCallWithRob={previousPortCallWithRob}
                            imoNumber={selectedVessel.imoNumber}
                        ></ReportRob>
                    )}
                </RightContentWrapper>
            )}
        </PageContentWrapper>
    );
};
export default TankCleaningComponent;
